import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiX, HiExternalLink, HiCalendar, HiAcademicCap } from "react-icons/hi";
import { HiCheckCircle } from "react-icons/hi";

const CertificationModal = ({ certification, onClose }) => {
    const { title, issuer, issue_date, expiry_date, image_url, description, credential_id, credential_url } = certification;

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleEsc);

        return () => {
            document.body.style.overflow = "unset";
            window.removeEventListener("keydown", handleEsc);
        };
    }, [onClose]);

    const formatDate = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString("es-ES", { year: "numeric", month: "long" });
    };

    const isExpired = expiry_date && new Date(expiry_date) < new Date();

    return (
        <>
            <AnimatePresence>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="fixed inset-0 bg-black/70 backdrop-blur-md z-[9999] flex items-center justify-center p-3 md:p-4 pt-20 md:pt-24"
                >
                <motion.div
                    initial={{ scale: 0.85, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.85, opacity: 0, y: 20 }}
                    transition={{ type: "spring", duration: 0.5, bounce: 0.3 }}
                    onClick={(e) => e.stopPropagation()}
                    className="max-w-7xl w-full max-h-[90vh] overflow-hidden relative p-6 rounded-3xl bg-gradient-to-br from-white/50 via-white/40 to-white/30 dark:from-gray-800/40 dark:via-gray-800/30 dark:to-gray-900/20 backdrop-blur-2xl border border-white/30 dark:border-white/10 shadow-[0_8px_32px_-8px_rgba(0,0,0,0.12),0_0_0_1px_rgba(255,255,255,0.15)_inset] dark:shadow-[0_8px_32px_-8px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.08)_inset]"
                >
                    {/* Close button */}
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 md:top-4 md:right-4 z-20 p-2 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors shadow-lg"
                        aria-label="Cerrar"
                    >
                        <HiX className="w-5 h-5 md:w-6 md:h-6 text-gray-700 dark:text-gray-300" />
                    </button>

                    <div className="relative z-10 flex flex-col md:grid md:grid-cols-7 max-h-[85vh] md:max-h-[90vh] overflow-y-auto">
                        {/* Left side - Image - 4 columnas */}
                        <div className="md:col-span-4 relative bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 p-6 md:p-6 flex items-center justify-center min-h-[200px] md:min-h-[550px]">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-purple-500/5 dark:from-blue-500/10 dark:to-purple-500/10"></div>

                            {image_url ? (
                                <motion.img
                                    initial={{ scale: 0.8, opacity: 0 }}
                                    animate={{ scale: 1, opacity: 1 }}
                                    transition={{ delay: 0.2 }}
                                    src={image_url}
                                    alt={title}
                                    className="relative z-10 w-auto h-auto object-contain rounded-2xl shadow-2xl max-w-[90%] md:max-w-md max-h-[40vh] md:max-h-[70vh]"
                                />
                            ) : (
                                <div className="relative z-10 w-24 h-24 md:w-32 md:h-32 flex items-center justify-center bg-white dark:bg-gray-700 rounded-full shadow-2xl">
                                    <HiAcademicCap className="w-12 h-12 md:w-16 md:h-16 text-blue-500 dark:text-blue-400" />
                                </div>
                            )}

                            {/* Decorative elements */}
                            <div className="absolute top-4 left-4 w-16 h-16 bg-blue-400/10 dark:bg-blue-400/5 rounded-full blur-2xl"></div>
                            <div className="absolute bottom-4 right-4 w-24 h-24 bg-purple-400/10 dark:bg-purple-400/5 rounded-full blur-3xl"></div>
                        </div>

                        {/* Right side - Content - 3 columnas */}
                        <div className="md:col-span-3 p-4 md:p-6 space-y-3 md:space-y-4">
                            {/* Header - más compacto */}
                            <div className="flex items-start gap-2 md:gap-3 pr-8 md:pr-12">
                                <div className="p-1.5 md:p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex-shrink-0">
                                    <HiCheckCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h2 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white leading-tight break-words">{title}</h2>
                                    <p className="text-xs md:text-sm text-blue-600 dark:text-blue-400 font-semibold mt-1">{issuer}</p>
                                </div>
                            </div>

                            {/* Dates section - en fila para ahorrar espacio */}
                            <div className="flex flex-col sm:flex-row gap-2">
                                <div className="flex items-center gap-2 p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-xl flex-1">
                                    <div className="p-1.5 bg-white dark:bg-gray-700 rounded-lg">
                                        <HiCalendar className="w-3.5 h-3.5 text-gray-600 dark:text-gray-300" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Emisión</p>
                                        <p className="text-xs font-medium text-gray-900 dark:text-white">{formatDate(issue_date)}</p>
                                    </div>
                                </div>

                                {expiry_date && (
                                    <div
                                        className={`flex items-center gap-2 p-2.5 rounded-xl flex-1 ${
                                            isExpired
                                                ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800"
                                                : "bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
                                        }`}
                                    >
                                        <div
                                            className={`p-1.5 rounded-lg ${
                                                isExpired ? "bg-red-100 dark:bg-red-900/40" : "bg-green-100 dark:bg-green-900/40"
                                            }`}
                                        >
                                            <HiCalendar
                                                className={`w-3.5 h-3.5 ${
                                                    isExpired ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"
                                                }`}
                                            />
                                        </div>
                                        <div>
                                            <p
                                                className={`text-xs font-semibold ${
                                                    isExpired ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"
                                                }`}
                                            >
                                                {isExpired ? "Expirada" : "Válida"}
                                            </p>
                                            <p className="text-xs font-medium text-gray-900 dark:text-white">{formatDate(expiry_date)}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Description - con max height y scroll si es muy largo */}
                            {description && (
                                <div className="space-y-1.5">
                                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Descripción</h3>
                                    <div className="max-h-20 md:max-h-24 overflow-y-auto pr-2 custom-scrollbar">
                                        <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed break-words">{description}</p>
                                    </div>
                                </div>
                            )}

                            {/* Credential */}
                            {credential_id && (
                                <div className="space-y-1.5">
                                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Credencial</h3>
                                    <div className="max-h-20 md:max-h-24 overflow-y-auto pr-2 custom-scrollbar">
                                        <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed break-all">{credential_id}</p>
                                    </div>
                                </div>
                            )}

                            {/* Action button - más compacto */}
                            {credential_url && (
                                <div className="pt-1">
                                    <a
                                        href={credential_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="group w-full inline-flex items-center justify-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 dark:from-blue-500 dark:to-blue-600 dark:hover:from-blue-600 dark:hover:to-blue-700 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                                    >
                                        <HiExternalLink className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:rotate-12 transition-transform flex-shrink-0" />
                                        <span className="text-xs truncate">Ver credencial oficial</span>
                                    </a>
                                </div>
                            )}

                            {!credential_url && (
                                <div className="pt-1">
                                    <div className="p-2 md:p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-700">
                                        <p className="text-xs text-gray-600 dark:text-gray-400 text-center break-words">
                                            Esta certificación no tiene una credencial digital verificable
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
                </motion.div>
            </AnimatePresence>

            {/* Estilos para el scrollbar personalizado */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(156, 163, 175, 0.3);
                    border-radius: 3px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(156, 163, 175, 0.5);
                }
            `}</style>
        </>
    );
};

export default CertificationModal;
